import type { SxProps } from '@mui/material'
import Box from '@mui/material/Box'
import GenericGridBackground from '@public//images/heros/header-grid-only-desktop.png'
import Image from 'next/image'
import { FC, ReactNode } from 'react'

import { RenderOn } from '@/utills/DeviceUtils'

export const animation = (index: number) => {
    return {
        transform: `translateY(${500 + index * 50}px)`,
        opacity: 0,
        transition: 'all .3s ease-in-out',
        '@keyframes move': {
            '0%': {
                opacity: 0,
                transform: `translateY(${500 + index * 50}px)`,
            },
            '12%': {
                opacity: 0,
            },
            '20%': {
                transform: 'translateY(-5px)',
            },
            '25%': {
                opacity: 1,
                transform: 'translateY(0)',
            },
            '88%': {
                opacity: 1,
                transform: 'translateY(0)',
            },
            '91%': {
                opacity: 1,
                transform: 'translateY(10px)',
            },
            [`${93 + index - 1}%`]: {
                opacity: 0,
            },
            '98%': {
                opacity: 0,
                transform: `translateY(-${100 + index * 50}px)`,
            },
        },
        animation: `move 7000ms ease-in-out ${index * 0.1}s infinite forwards`,
        img: {
            filter: 'drop-shadow(0px 16px 72px rgba(0, 0, 0, 0.10))',
        },
    }
}

const Hero: FC<{
    chips?: {
        items: {
            src: string
            width: number
            height: number
            sx?: SxProps
        }[]
        gap: number
    }
    backgroundImageDesktop?: string
    backgroundImageMobile?: string
    imageAlt?: string
    desktopImageSize?: { width: number; height: number }
    mobileImageSize?: { width: number; height: number }
    animatedDesktopHero?: ReactNode
    children: ReactNode
}> = ({
    backgroundImageMobile,
    backgroundImageDesktop,
    children,
    chips,
    imageAlt,
    desktopImageSize,
    mobileImageSize,
    animatedDesktopHero,
}) => {
    return (
        <Box
            sx={{
                pb: {
                    xs: backgroundImageMobile ? '440px' : '0',
                    md: '0',
                },
                height: {
                    xs: 'auto',
                    md: '792px',
                },
                mt: {
                    xs: backgroundImageMobile ? '-72px !important' : '0',
                    md: '-72px !important',
                },
                position: 'relative',
                '.background-image': {
                    height: '100%',
                    width: 'auto',
                    position: 'absolute',
                    left: '-100%',
                    right: '-100%',
                    margin: 'auto',
                    top: 0,
                    zIndex: '-1',
                },
                '.hero-animation-frame': {
                    height: '100%',
                    width: 'auto',
                    aspectRatio: '3840 / 1520',
                    position: 'absolute',
                    top: 0,
                    left: '50%',
                    right: 'auto',
                    margin: 0,
                    transform: 'translateX(-50%)',
                    zIndex: -1,
                    overflow: 'hidden',
                },
            }}
        >
            {backgroundImageMobile && (
                <RenderOn breakPoints={['xs', 'sm']}>
                    <Image
                        className={'background-image'}
                        width={mobileImageSize?.width ?? 1800}
                        height={mobileImageSize?.height ?? 1440}
                        src={backgroundImageMobile}
                        alt={imageAlt ?? 'page-hero-mobile'}
                    />
                </RenderOn>
            )}
            <RenderOn breakPoints={['md', 'lg', 'xl']}>
                {animatedDesktopHero ? (
                    <Box className="background-image hero-animation-frame">
                        {animatedDesktopHero}
                    </Box>
                ) : (
                    <Image
                        className={'background-image'}
                        width={desktopImageSize?.width ?? 4000}
                        height={desktopImageSize?.height ?? 1584}
                        src={backgroundImageDesktop || GenericGridBackground}
                        alt={imageAlt ?? 'page-hero-desktop'}
                    />
                )}
                <Box
                    sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                    }}
                >
                    {children}
                    {chips && (
                        <Box
                            sx={{
                                width: '45%',
                                display: 'flex',
                                alignItems: 'center',
                                gap: chips.gap,
                                flexDirection: 'column',
                            }}
                        >
                            {chips.items.map((chip, index) => (
                                <Box key={`chip-${index}`} sx={{ ...animation(index) }}>
                                    <Image
                                        alt={`chip-${index}`}
                                        src={chip}
                                        width={chip.width}
                                        height={chip.height}
                                    />
                                </Box>
                            ))}
                        </Box>
                    )}
                </Box>
            </RenderOn>
            <RenderOn breakPoints={['xs', 'sm']}>{children}</RenderOn>
        </Box>
    )
}

export default Hero
